/* eslint-disable import/prefer-default-export */
import { useCallback } from "react"
import { AssetChain } from "@mezo/sdk"
import { readContracts } from "@wagmi/core"
import { useAccount, useConfig } from "wagmi"

import { setWalletBalances, useDappDispatch } from "#/redux-state"
import { MEZO_ASSETS, MEZO_CHAIN } from "#/shared/constants"

type TokenToFetch = keyof typeof MEZO_ASSETS

export const useMezoAssetsBalance = () => {
  const config = useConfig()
  const { address } = useAccount()
  const dispatch = useDappDispatch()

  const getTokenContracts = useCallback(
    (tokens: TokenToFetch[]) =>
      tokens.map((tokenKey) => {
        const { address: tokenAddress, abi } = MEZO_ASSETS[tokenKey]!
        return {
          abi,
          functionName: "balanceOf",
          address: tokenAddress,
          args: [address!],
          query: { enabled: !!address },
          chainId: MEZO_CHAIN.id,
        }
      }),
    [address],
  )

  const fetchMezoAssetsBalance = useCallback(
    async (tokens?: TokenToFetch[]) => {
      // @ts-expect-error Figure out why types don't match
      const tokenData = await readContracts(config, {
        contracts: getTokenContracts(
          tokens ?? (Object.keys(MEZO_ASSETS) as TokenToFetch[]),
        ),
      })

      const tokenBalances = tokenData?.map((balance, index) => ({
        name: tokens ? tokens[index] : Object.keys(MEZO_ASSETS)[index],
        balance: BigInt((balance.result as string | undefined) ?? "0"),
      }))

      if (tokenBalances) {
        const walletBalances = Object.fromEntries(
          tokenBalances.map((token) => [
            token.name,
            {
              balance: token.balance,
              decimals:
                MEZO_ASSETS[token.name as TokenToFetch]?.decimals ?? 18n,
              chain: AssetChain.MEZO,
            },
          ]),
        )

        dispatch(setWalletBalances(walletBalances))
      }

      return tokenBalances
    },
    [config, getTokenContracts, dispatch],
  )

  return { fetchMezoAssetsBalance }
}
